import axios, { isAxiosError } from 'axios'

const IDEMPOTENT_METHODS = new Set(['get', 'head', 'options'])

export const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

export const isAxiosCanceledError = (error: unknown): boolean => {
  if (axios.isCancel(error)) {
    return true
  }

  if (!isAxiosError(error)) {
    return false
  }

  return (
    error.code === 'ERR_CANCELED' ||
    error.name === 'CanceledError' ||
    error.message?.toLowerCase() === 'canceled' ||
    Boolean(error.config?.signal?.aborted)
  )
}

export const isAxiosNetworkError = (error: unknown): boolean => {
  if (!isAxiosError(error) || isAxiosCanceledError(error)) {
    return false
  }

  return (
    error.code === 'ERR_NETWORK' ||
    error.message === 'Network Error' ||
    error.code === 'ECONNABORTED'
  )
}

export const isRetryableNetworkError = (error: unknown): boolean => {
  if (!isAxiosError(error) || isAxiosCanceledError(error)) {
    return false
  }

  if (error.response) {
    return false
  }

  const method = (error.config?.method ?? 'get').toLowerCase()
  if (!IDEMPOTENT_METHODS.has(method)) {
    return false
  }

  return (
    error.code === 'ERR_NETWORK' ||
    error.message === 'Network Error' ||
    error.code === 'ECONNABORTED'
  )
}

/**
 * Errors that should not inflate RUM (aborted requests, offline browser).
 * Callers may still reject; Datadog beforeSend and logAxiosError use this.
 */
export const shouldSuppressAxiosErrorForRum = (
  error: unknown,
  options?: { online?: boolean }
): boolean => {
  if (isAxiosCanceledError(error)) {
    return true
  }

  const online =
    options?.online ??
    (typeof navigator === 'undefined' ? true : navigator.onLine !== false)

  if (!online && isAxiosNetworkError(error)) {
    return true
  }

  return false
}

/**
 * Prefer this over raw console.error for Axios failures so canceled / offline
 * noise does not flood Datadog RUM (issue 004).
 */
export const logAxiosError = (error: unknown, context?: string): void => {
  if (shouldSuppressAxiosErrorForRum(error)) {
    return
  }

  if (context) {
    console.error(context, error)
    return
  }

  console.error(error)
}

export const isAxiosErrorMessageNetworkError = (message: string): boolean => {
  const normalized = message.toLowerCase()
  return (
    normalized.includes('network error') ||
    normalized.includes('err_network') ||
    normalized.includes('econnaborted')
  )
}

export const isAxiosErrorMessageCanceled = (message: string): boolean => {
  const normalized = message.toLowerCase()
  return (
    normalized.includes('canceled') ||
    normalized.includes('cancelled') ||
    normalized.includes('err_canceled') ||
    normalized.includes('cancelederror')
  )
}

/** Expected MSAL refresh path — must not inflate RUM as an error (issue 009). */
export const isMsalTokenRefreshMessage = (message: string): boolean =>
  message
    .toLowerCase()
    .includes('access token expired, triggering msal refresh')

/** Benign ResizeObserver loop browser warning — RUM noise only (issue 019). */
export const isResizeObserverLoopMessage = (message: string): boolean =>
  message.toLowerCase().includes('resizeobserver loop')

/** `focus-trap-react` teardown race reading `tabbableNodes` (issue 010). */
export const isFocusTrapTeardownMessage = (message: string): boolean =>
  message.toLowerCase().includes('tabbablenodes')

/** Message-level mirror of Datadog beforeSend filter (issues 004, 009, 010, 019). */
export const shouldDropRumErrorMessage = (
  message: string,
  options?: { online?: boolean }
): boolean => {
  if (!message) {
    return false
  }

  if (isAxiosErrorMessageCanceled(message)) {
    return true
  }

  if (isMsalTokenRefreshMessage(message)) {
    return true
  }

  if (isResizeObserverLoopMessage(message)) {
    return true
  }

  if (isFocusTrapTeardownMessage(message)) {
    return true
  }

  const online =
    options?.online ??
    (typeof navigator === 'undefined' ? true : navigator.onLine !== false)

  return !online && isAxiosErrorMessageNetworkError(message)
}

/**
 * Extract a user-facing message from Axios 400 (and other) responses (RUM 005).
 */
export const getAxiosErrorMessage = (
  error: unknown,
  fallback = 'Something went wrong'
): string => {
  if (!isAxiosError(error)) {
    if (error instanceof Error && error.message) {
      return error.message
    }
    return fallback
  }

  const data = error.response?.data as
    | { message?: string | string[]; error?: string }
    | string
    | undefined

  if (typeof data === 'string' && data.trim()) {
    return data.trim()
  }

  if (data && typeof data === 'object') {
    const message = data.message
    if (Array.isArray(message) && message[0]) {
      return String(message[0])
    }
    if (typeof message === 'string' && message.trim()) {
      return message.split('\n')[0].trim()
    }
    if (typeof data.error === 'string' && data.error.trim()) {
      return data.error.trim()
    }
  }

  return fallback
}

/**
 * True when the Axios error is an HTTP 400 Bad Request.
 */
export const isAxiosBadRequestError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 400

/**
 * True when the Axios error is an HTTP 409 Conflict (RUM 007/014).
 */
export const isAxiosConflictError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 409

/**
 * True when the Axios error is an HTTP 500 Internal Server Error (RUM 011).
 */
export const isAxiosServerError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 500

export const CONFLICT_SAVE_FALLBACK =
  'Record updated by another user — refresh to continue'

export const CONFLICT_DOWNLOAD_FALLBACK = 'File busy — try again in a moment'

export const SERVER_ERROR_LIST_FALLBACK =
  'Unable to load records. Please try again.'

/**
 * Build entities filter URL; keeps query params well-formed (RUM 005).
 */
export const buildEntitiesFilterUrl = (
  baseUrl: string,
  page: number,
  size: string | number,
  type?: string
): string => {
  const params = new URLSearchParams()
  params.set('page', String(page))
  params.set('size', String(size))
  if (type != null && String(type).length > 0) {
    params.set('type', String(type))
  }
  return `${baseUrl}?${params.toString()}`
}
